import { NgClass } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LazyLoadEvent, PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { SelectButton } from 'primeng/selectbutton';
import { Tooltip } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ServiceResult } from '../../../core/interfaces/service-result';
import { SpsTableComponent } from '../../../custom/components/sps-table/sps-table.component';
import { SpsTransactionDetailsDialogComponent } from '../../../custom/components/sps-transaction-details-dialog/sps-transaction-details-dialog.component';
import { TransactionsPollingService } from '../../services/polling/transactions-polling.service';
import { BaseTransactionViewComponent } from '../base/base-transaction-view.component';

@Component({
  selector: 'app-transactions-view',
  imports: [
    Card,
    Button,
    PrimeTemplate,
    SelectButton,
    FormsModule,
    SpsTableComponent,
    NgClass,
    Dialog,
    SpsTransactionDetailsDialogComponent,
    Tooltip
  ],
  templateUrl: './transactions-view.component.html'
})
export class TransactionsViewComponent extends BaseTransactionViewComponent implements OnInit, AfterViewInit, OnDestroy {

  protected tableConfigKeyName = 'SPS_TRANSACTIONS_VIEW';
  protected tableRowsKeyName = 'TRANSACTIONS_VIEW_ROWS';
  protected tableStateKeyName = 'TRANSACTIONS_VIEW_STATE';
  public transactionViewMode: 'TENANT' | 'ADMIN' = 'TENANT';

  readonly exportRunning: WritableSignal<boolean> = signal(false);

  /**
   * Params for Data-Refresh-Polling
   * (only for the tenant-view, no polling in admin-view)
   */
  pollingService = inject(TransactionsPollingService);
  lastDataHash: string = '';

  override async ngOnInit() {
    await super.ngOnInit();
  }

  override ngAfterViewInit(): void {

    super.ngAfterViewInit();

    /**
     * Initialize Breadcrumb-Items
     */
    setTimeout(() => {
      this.i18n.breadcrumbMenuItems.set([
        {
          icon: 'pi pi-home',
          i18nLabelKey: 'MENUDASHBOARD',
          label: this.i18n.i18nLng()['MENUDASHBOARD'],
          routerLink: ['/dashboard'],
          routerLinkActive: false,
        },
        {
          i18nLabelKey: 'MENUTRANSACTIONS',
          label: this.i18n.i18nLng()['MENUTRANSACTIONS'],
          routerLinkActive: true
        },
      ]);
    }, 50);
  }

  override ngOnDestroy() {
    this.pollingService.unsubscribePolling();
    super.ngOnDestroy();
  }

  /**
   * Parent-Data-Provider
   * @param lazyLoadEvent
   */
  getTransactionPageData = (lazyLoadEvent: LazyLoadEvent) => {
    this.lastLazyLoadingEvent = lazyLoadEvent;
    return this.transactionDataService.findPage(lazyLoadEvent, this.currentState);
  }

  /**
   * DataMapper for lazy loaded table-data
   * @param serviceResult
   */
  transactionDataMapper = (serviceResult: ServiceResult) => {
    try {
      this.lastDataHash = this.helper.dataArrayHash(serviceResult.payload);
      serviceResult.payload.map(transactionData => {
        return this.transactionDataMapperService.map(transactionData);
      });
      /**
       * Subscribe the polling service for incoming transactions
       */
      if (!this.pollingService.isSubscribed) {
        console.log('Subscribing Polling-Service');
        this.pollingService.subscribePolling(this);
      }
      return serviceResult;
    } catch (e) {
      return null;
    }
  }

  /**
   * Export current transaction data as csv file
   * @protected
   */
  protected exportTransactionData() {
    this.exportRunning.set(true);
    this.layoutService.progressDialogTitle = this.i18n.i18nLng()['EXPORTDATA'];
    this.layoutService.showProgressSpinner = true;
    this.layoutService.showProgressDialog = true;
    const exportUrl: string = this.transactionDataService.baseUrl + '/transactiondata/export?search=' + encodeURIComponent(JSON.stringify(this.lastLazyLoadingEvent)) + '&status=' + this.currentState;
    const subscription: Subscription = this.fileService.downloadFile(exportUrl).subscribe({
      next: success => {
        this.layoutService.showProgressDialog = false;
        this.exportRunning.set(false);
        subscription.unsubscribe();
        if (success) {
          this.messageService.add({
            life: 3500,
            severity: 'success',
            summary: this.i18n.i18nLng()['FILEDOWNLOADTITLE'],
            detail: this.i18n.i18nLng()['FILEDOWNLOADSUCCESS']
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.fileService?.errorHandler?.message,
            detail: this.fileService?.errorHandler?.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      }
    });
  }

  /**
   * Refund-Transaction was successful
   * @protected
   */
  protected onRefundSuccessful() {
    this.closeDetailsDialog();
    this.messageService.add({
      life: 3500,
      severity: 'success',
      summary: this.i18n.i18nLng()['REFUND'],
      detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
    });
  }

  protected onCreditSuccessful() {
    this.closeDetailsDialog();
    this.messageService.add({
      life: 3500,
      severity: 'success',
      summary: this.i18n.i18nLng()['CHARGEBACK'],
      detail: this.i18n.i18nLng()['TRANSACTIONSUCCESSFUL_MSG']
    });
  }
}
